import { getAPIURL } from "./config";

const readErrorMessage = async (response, fallback) => {
  const errorText = await response.text();
  if (response.status === 422) {
    try {
      const err = JSON.parse(errorText);
      const details = Array.isArray(err.detail)
        ? err.detail.map((item) => item.msg || JSON.stringify(item)).join("; ")
        : err.detail;
      if (details) return `Ошибка валидации: ${details}`;
    } catch {
      if (errorText) return `${fallback}. ${errorText}`;
    }
  }
  return errorText ? `${fallback}. ${errorText}` : fallback;
};

const request = async (path, { method = "GET", body, form, signal, accept } = {}) => {
  const headers = { Accept: accept || "application/json" };
  let payload;
  if (form) {
    payload = form;
  } else if (body !== undefined) {
    headers["Content-Type"] = "application/json";
    payload = JSON.stringify(body);
  }

  const response = await fetch(`${getAPIURL()}${path}`, {
    method,
    headers,
    body: payload,
    credentials: "include",
    signal,
  });

  if (!response.ok) {
    throw new Error(await readErrorMessage(response, `Ошибка ${method} ${path}: ${response.status}`));
  }
  if (response.status === 204) return null;
  const contentType = response.headers.get("content-type") || "";
  if (!contentType.includes("json")) return response;
  return response.json();
};

const downloadBlob = (blob, filename) => {
  const downloadUrl = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = downloadUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(downloadUrl);
};

const isZip = (bytes) => bytes?.length > 1 && bytes[0] === 0x50 && bytes[1] === 0x4b;

const toExcelBlob = (buffer) => {
  const bytes = new Uint8Array(buffer);
  if (isZip(bytes)) {
    return new Blob([bytes], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
  }
  const text = new TextDecoder().decode(bytes).trim();
  let payload = text;
  if (text.startsWith('"') || text.startsWith("{")) {
    try {
      payload = JSON.parse(text);
    } catch {
      payload = text;
    }
  }
  if (typeof payload === "string") {
    const base64 = payload.replace(/^data:.*?;base64,/, "").replace(/\s/g, "");
    try {
      const binary = Uint8Array.from(atob(base64), (char) => char.charCodeAt(0));
      if (isZip(binary)) {
        return new Blob([binary], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
      }
    } catch {
      // не base64
    }
  }
  return null;
};

const downloadExcelResponse = async (response, fallbackName) => {
  const blob = toExcelBlob(await response.arrayBuffer());
  if (!blob) throw new Error("Сервер вернул не файл Excel");
  const disposition = response.headers.get("content-disposition") || "";
  const encoded = disposition.match(/filename\*=(?:UTF-8''|utf-8'')([^;]+)/i);
  let filename = fallbackName;
  if (encoded?.[1]) {
    try {
      filename = decodeURIComponent(encoded[1].trim().replace(/^["']|["']$/g, ""));
    } catch {
      filename = encoded[1];
    }
  }
  downloadBlob(blob, filename.endsWith(".xlsx") ? filename : `${filename}.xlsx`);
};

const queryString = (entries) => {
  const params = new URLSearchParams();
  Object.entries(entries).forEach(([key, value]) => {
    if (value == null || value === "") return;
    params.set(key, String(value));
  });
  const query = params.toString();
  return query ? `?${query}` : "";
};

/** GET /finance/dictionaries/{code}/items?q= */
export const getDictionaryItems = (code, { q, limit = 50, signal } = {}) =>
  request(
    `finance/dictionaries/${encodeURIComponent(code)}/items${queryString({ q, limit })}`,
    { signal }
  );

export const getJournalColumns = (signal) =>
  request("finance/journal/columns", { signal });

export const getJournalEntry = (entryId, signal) =>
  request(`finance/journal/${encodeURIComponent(entryId)}`, { signal });

export const getJournalHistory = (entryId, signal) =>
  request(`finance/journal/${encodeURIComponent(entryId)}/history`, { signal });

export const getUnmappedValues = (params = {}, signal) =>
  request(`finance/journal/unmapped${queryString(params)}`, { signal });

/** PATCH /finance/journal/{id}/corr */
export const patchJournalCorrection = (entryId, body) =>
  request(`finance/journal/${encodeURIComponent(entryId)}/corr`, {
    method: "PATCH",
    body,
  });

/** DELETE /finance/journal/{id}/corr */
export const resetJournalCorrection = (entryId, reason) =>
  request(`finance/journal/${encodeURIComponent(entryId)}/corr`, {
    method: "DELETE",
    body: { reason: reason || null },
  });

export const triggerJournalSync = ({ wait = true } = {}) =>
  request(`finance/journal/sync?wait=${wait ? "true" : "false"}`, { method: "POST" });

export const getJournalSyncStatus = (signal) =>
  request("finance/journal/sync/status", { signal });

export const getJournalSyncRuns = ({ limit = 20, signal } = {}) =>
  request(`finance/journal/sync/runs${queryString({ limit })}`, { signal });

/** POST /finance/import/1c-uo multipart: base, file, run_sync */
export const importOneCReport = ({ base, file, runSync = true }) => {
  const form = new FormData();
  form.append("base", base);
  form.append("file", file);
  form.append("run_sync", runSync ? "true" : "false");
  return request("finance/import/1c-uo", { method: "POST", form });
};

export const getRules = ({ ruleType, signal } = {}) =>
  request(`finance/rules${queryString({ rule_type: ruleType })}`, { signal });

export const getRuleFields = (signal) => request("finance/rules/fields", { signal });

export const previewRule = (body, { dateFrom, dateTo }) =>
  request(
    `finance/rules/preview${queryString({ date_from: dateFrom, date_to: dateTo })}`,
    { method: "POST", body }
  );

export const createRule = (body) => request("finance/rules", { method: "POST", body });

export const updateRule = (ruleId, body) =>
  request(`finance/rules/${encodeURIComponent(ruleId)}`, { method: "PUT", body });

export const deleteRule = (ruleId) =>
  request(`finance/rules/${encodeURIComponent(ruleId)}`, { method: "DELETE" });

export const reorderRules = (body) =>
  request("finance/rules/order", { method: "PUT", body });

export const getReportFields = (signal) => request("finance/reports/fields", { signal });

export const buildReport = (report, body) =>
  request(`finance/reports/${encodeURIComponent(report)}`, { method: "POST", body });

export const exportReportExcel = async (report, body) => {
  const response = await fetch(`${getAPIURL()}finance/reports/${encodeURIComponent(report)}/excel`, {
    method: "POST",
    headers: {
      Accept:
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/octet-stream, application/json",
      "Content-Type": "application/json",
    },
    credentials: "include",
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    throw new Error(await readErrorMessage(response, "Не удалось выгрузить отчёт"));
  }
  await downloadExcelResponse(response, `${report}.xlsx`);
};

export const previewAllocation = (body) =>
  request("finance/allocations/preview", { method: "POST", body });

export const createAllocation = (body) =>
  request("finance/allocations", { method: "POST", body });

export const getAllocations = ({ status, limit = 50, signal } = {}) =>
  request(`finance/allocations${queryString({ status, limit })}`, { signal });

export const getAllocation = (runId, signal) =>
  request(`finance/allocations/${encodeURIComponent(runId)}`, { signal });

export const getAllocationEntries = (runId, { limit = 200, offset = 0, signal } = {}) =>
  request(
    `finance/allocations/${encodeURIComponent(runId)}/entries${queryString({ limit, offset })}`,
    { signal }
  );

export const deleteAllocation = (runId) =>
  request(`finance/allocations/${encodeURIComponent(runId)}`, { method: "DELETE" });

export const rerunAllocation = (runId, body) =>
  request(`finance/allocations/${encodeURIComponent(runId)}/rerun`, {
    method: "POST",
    body: body || null,
  });

export const getRecognitionList = (kind, signal) =>
  request(`finance/recognition/${kind}/list`, { signal });

export const addRevenueProject = (body) =>
  request("finance/recognition/revenue/list", { method: "POST", body });

export const addExpenseContract = (body) =>
  request("finance/recognition/expense/list", { method: "POST", body });

export const removeRecognitionItem = (kind, itemId) =>
  request(`finance/recognition/${kind}/list/${encodeURIComponent(itemId)}`, {
    method: "DELETE",
  });

export const getRecognitionDocuments = ({ kind, project, signal } = {}) =>
  request(`finance/recognition/documents${queryString({ kind, project })}`, { signal });

export const previewRecognitionDocument = (body) =>
  request("finance/recognition/documents/preview", { method: "POST", body });

export const createRecognitionDocument = (body) =>
  request("finance/recognition/documents", { method: "POST", body });

export const deleteRecognitionDocument = (docId) =>
  request(`finance/recognition/documents/${encodeURIComponent(docId)}`, {
    method: "DELETE",
  });

export const getPeriods = ({ year, signal } = {}) =>
  request(`finance/periods${queryString({ year })}`, { signal });

export const closePeriod = (year, month, reason) =>
  request(`finance/periods/${year}/${month}/close`, {
    method: "POST",
    body: { reason: reason || null },
  });

export const openPeriod = (year, month, reason) =>
  request(`finance/periods/${year}/${month}/open`, {
    method: "POST",
    body: { reason: reason || null },
  });
